import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { makeStyles } from "tss-react/mui";
import NavDetails from "components/Elements/NavDetails";
import PrintIcon from "assets/printer.svg";
import { defectReportTypes } from "constants/ReportTypes";

import {
	Dialog,
	DialogContent,
	DialogTitle,
	TextField,
} from "@mui/material";
import AddDialogStyle from "styles/application/AddDialogStyle";
import CommonDropdown from "components/Elements/CommonDropdown";
import {
	ScheduleReportModal,
	buildScheduleReportFooterItem,
	useScheduleReportModal,
	ReportGenerationDialog,
} from "components/Modules/ReportSubscriptions";
import { useCreateReportSubscription } from "components/Modules/ReportSubscriptions/hooks/useCreateReportSubscription";
import { useFilters } from "components/Elements/Filters/FilterContext";
import {
	formValuesToApiRequest,
	toApiReportType,
} from "services/reportSubscriptions";

const ADD = AddDialogStyle();

const media = "@media (max-width: 414px)";

const useStyles = makeStyles()((theme) => ({
	restore: {
		border: "2px solid",
		borderRadius: "100%",
		height: "35px",
		width: "35px",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		color: "#307ad6",
	},
	importButton: {
		background: "#ED8738",
	},
	buttons: {
		display: "flex",
		marginLeft: "auto",
		[media]: {
			marginLeft: "0px",
			flexDirection: "column",
			marginBottom: "10px",
			gap: "10px",
		},
	},
	wrapper: {
		display: "flex",
		alignItems: "flex-start",
		[media]: {
			marginTop: "10px",
			justifyContent: "space-between",
			flexDirection: "column",
		},
		gap: "2rem",
	},
	dropdownContainer: {
		width: "22rem",
		marginTop: "-4px",
	},
	crumbRow: {},
}));

function Header({
	dataLength,
	defectsCC,
	customCaptions,
}) {
	const { t } = useTranslation();
	const { classes } = useStyles();
	const [workOrderPopup, setWorkOrderPopup] = useState(false);
	const [workOrder, setWorkOrder] = useState("");
	const [pendingWorkOrderReport, setPendingWorkOrderReport] = useState(null);

	const { filterValues, buildReportFilterContext } = useFilters();
	const [activeReportRequest, setActiveReportRequest] = useState(null);

	const closeReportDialog = () => setActiveReportRequest(null);

	const closePopup = () => {
		setWorkOrder("");
		setWorkOrderPopup(false);
		setPendingWorkOrderReport(null);
	};

	const serviceWorkOrderCaption =
		customCaptions.serviceWorkOrder || "Work Order";

	const defectReports = [
		{
			id: "1",
			name: t("option.partsByWorkOrderReport", {
				entity: customCaptions.defect || "Defect",
				parts: customCaptions.partPlural || "Parts",
				workOrder: serviceWorkOrderCaption,
			}),
			reportType: defectReportTypes?.PARTSBYWORKORDER,
		},
		{
			id: "2",
			name: t("option.entityReport", {
				entity: customCaptions.defectPlural || "Defects",
			}),
			reportType: defectReportTypes?.DEFECTSSUMMARY,
		},
		{
			id: "3",
			name: t("option.entityByStatus", {
				entity: customCaptions.defectPlural || "Defects",
			}),
			reportType: defectReportTypes?.DEFECTSBYSTATUS,
		},
		{
			id: "4",
			name: t("option.entityByRisk", {
				entity: customCaptions.defectPlural || "Defects",
			}),
			reportType: defectReportTypes?.DEFECTSBYRISK,
		},
		{
			id: "5",
			name: t("option.entityExcel", {
				entity: customCaptions.defectPlural || "Defects",
			}),
			reportType: defectReportTypes?.DEFECTSEXCEL,
		},
	];

	const scheduleModal = useScheduleReportModal();
	const createSubscription = useCreateReportSubscription();
	const handleScheduleSubmit = async (payload) => {
		const apiBody = formValuesToApiRequest(payload, payload.filterValues, payload.filterSummary);
		await createSubscription(apiBody);
	};

	const startReport = (report, extraFilterContext = null) => {
		const flat = buildReportFilterContext();
		setActiveReportRequest({
			reportName: report.name,
			body: {
				reportType: toApiReportType(report.reportType),
				filterContext: extraFilterContext ? { ...flat, ...extraFilterContext } : flat,
				name: null,
				recipientEmail: null,
				sendEmail: false,
			},
		});
	};

	const handleReportClick = (report, handleClose) => {
		if (!report || Object.keys(report).length < 1) return;
		handleClose();
		if (report.reportType === defectReportTypes?.PARTSBYWORKORDER) {
			setPendingWorkOrderReport(report);
			setWorkOrderPopup(true);
			return;
		}
		startReport(report);
	};

	const handleWorkOrderSubmit = () => {
		if (!pendingWorkOrderReport) return;
		startReport(pendingWorkOrderReport, { workOrder });
		closePopup();
	};

	return (
		<div className={"topContainerCustomCaptions"}>
			<NavDetails
				status={false}
				lastSaved={""}
				staticCrumbs={[
					{
						id: 1,
						name: `${defectsCC} (${dataLength})`,
						url: "",
					},
				]}
				hideLastLogin
				hideLastSave
				hideVersion={true}
			/>
			{activeReportRequest ? (
				<ReportGenerationDialog
					open
					onClose={closeReportDialog}
					reportName={activeReportRequest.reportName}
					body={activeReportRequest.body}
				/>
			) : null}
			<Dialog
				open={workOrderPopup}
				onClose={closePopup}
				aria-labelledby="alert-dialog-title"
				aria-describedby="alert-dialog-description"
			>
				<ADD.ActionContainer>
					<DialogTitle id="alert-dialog-title">
						{
							<ADD.HeaderText>
								{t("title.enterEntity", { entity: serviceWorkOrderCaption })}
							</ADD.HeaderText>
						}
					</DialogTitle>
					<ADD.ButtonContainer>
						<ADD.CancelButton onClick={closePopup} variant="contained">
							{t("action.cancel")}
						</ADD.CancelButton>
						<ADD.ConfirmButton
							onClick={handleWorkOrderSubmit}
							variant="contained"
							className={classes.createButton}
							disabled={!workOrder}
						>
							{t("action.generate")}
						</ADD.ConfirmButton>
					</ADD.ButtonContainer>
				</ADD.ActionContainer>
				<DialogContent className={classes.dialogContent}>
					<TextField
						sx={{
							"& .MuiInputBase-input.Mui-disabled": {
								WebkitTextFillColor: "#000000",
							},
						}}
						label={serviceWorkOrderCaption}
						fullWidth
						multiline
						onChange={(e) => setWorkOrder(e.target.value)}
					/>
				</DialogContent>
			</Dialog>
			<div className={classes.wrapper}>
				<CommonDropdown
					iconSrc={PrintIcon}
					isDownloading={false}
					dataSource={defectReports}
					dynamicAction={handleReportClick}
					footerItem={buildScheduleReportFooterItem(scheduleModal.openModal, t("action.scheduleEmailReport"))}
				/>
			</div>
			{scheduleModal.open ? (
				<ScheduleReportModal
					open
					onClose={scheduleModal.closeModal}
					availableReports={defectReports}
					initialValues={scheduleModal.initialValues}
					onSubmit={handleScheduleSubmit}
				/>
			) : null}
		</div>
	);
}

export default Header;
